import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import ConfirmModal from '../../app/modals/ConfirmModal';
import { CAMPAIGN_STATUS, CAMPAIGN_STATUS_CLASS_NAME } from '../../app/writerRegisterCampaign/presenters/WriterRegisterCampaignDetailPresenter';
import images from '../../assets/images';
import Card from '../../components/Card';
import Pagination from '../../components/Pagination';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import { PintoProductCampaignStatus } from '../../core/graphql/types';
import useMutationDeletePintoWriterCampaign from '../../hooks/useMutationDeletePintoWriterCampaign';
import useQueryPintoWriterCampaigns from '../../hooks/useQueryPintoWriterCampaigns';

const PAGE_LIMIT = 10;

const WriterRegisterCampaignPage = () => {
  const { pintoWriterCampaigns, fetchPintoWriterCampaigns, isLoadingPintoWriterCampaigns } = useQueryPintoWriterCampaigns();
  const { onMutateDeletePintoWriterCampaign } = useMutationDeletePintoWriterCampaign();

  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | undefined>(undefined);
  const [previewCampaignId, setPreviewCampaignId] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const campaigns = useMemo(() => pintoWriterCampaigns?.edges?.map(({ node }) => node) || [], [pintoWriterCampaigns?.edges]);

  const handleDeleteClick = useCallback((campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setShowConfirmDelete(true);
  }, []);

  const onConfirmDelete = useCallback(async () => {
    if (!selectedCampaignId) return;

    const res = await onMutateDeletePintoWriterCampaign(selectedCampaignId);

    if (res?.data?.deletePintoWriterCampaign?.success) {
      ToastComponent({ label: 'ลบแคมเปญเรียบร้อยแล้ว', type: IToastType.SUCCESS });
      setCurrentPage(1);
      fetchPintoWriterCampaigns();
    } else {
      ToastComponent({ label: res?.data?.deletePintoWriterCampaign?.message || 'ลบแคมเปญไม่สำเร็จ', type: IToastType.ERROR });
    }

    setShowConfirmDelete(false);
    setSelectedCampaignId(undefined);
  }, [fetchPintoWriterCampaigns, onMutateDeletePintoWriterCampaign, selectedCampaignId]);

  const onCancelDelete = useCallback(() => {
    setShowConfirmDelete(false);
    setSelectedCampaignId(undefined);
  }, []);

  const formatDate = (date: string) => {
    return dayjs(date).format('DD/MM/YYYY');
  };

  const clearPreviewTimer = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  }, []);

  const handlePreviewMouseEnter = useCallback(
    (campaignId: string) => {
      clearPreviewTimer();
      setPreviewCampaignId(undefined);

      previewTimerRef.current = setTimeout(() => {
        setPreviewCampaignId(campaignId);
      }, 1000);
    },
    [clearPreviewTimer],
  );

  const handlePreviewMouseLeave = useCallback(() => {
    clearPreviewTimer();
    setPreviewCampaignId(undefined);
  }, [clearPreviewTimer]);

  useEffect(() => {
    return () => {
      clearPreviewTimer();
    };
  }, [clearPreviewTimer]);

  const getCampaignTitleLink = useCallback((campaignId: string, status: PintoProductCampaignStatus) => {
    if (status === PintoProductCampaignStatus.Pending) return `/writerRegisterCampaign/edit?id=${campaignId}`;
    return `/writerRegisterCampaign/edit?id=${campaignId}&mode=view`;
  }, []);

  const handleChangePage = useCallback(
    (page: number) => {
      if (page === currentPage || page < 1) return;

      const offset = (page - 1) * PAGE_LIMIT;

      setCurrentPage(page);
      fetchPintoWriterCampaigns({
        limit: PAGE_LIMIT,
        offset,
      });
    },
    [currentPage, fetchPintoWriterCampaigns],
  );

  return (
    <div className="w-full">
      {showConfirmDelete && (
        <ConfirmModal
          title="ลบแคมเปญ"
          subTitle="คุณแน่ใจหรือไม่ว่าต้องการลบแคมเปญนี้? การกระทำนี้ไม่สามารถย้อนกลับได้"
          onConFirm={onConfirmDelete}
          onClose={onCancelDelete}
          confirmText="ลบ"
          cancelText="ยกเลิก"
        />
      )}

      <div className="flex items-center justify-between mb-24">
        <h1 className="font-dbh text-[28px]">แคมเปญ Writer Register</h1>
        <Link to="/writerRegisterCampaign/edit">
          <button className="gap-8 btn btn-error">
            <FiPlus />
            สร้างแคมเปญ
          </button>
        </Link>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="table w-full min-w-[900px]">
            <thead>
              <tr>
                <th className="text-systemGrays02LabelSecondary">ชื่อแคมเปญ</th>
                <th className="text-systemGrays02LabelSecondary">ช่วงรับสมัคร</th>
                <th className="text-systemGrays02LabelSecondary">ช่วงแคมเปญ</th>
                <th className="text-systemGrays02LabelSecondary">ส่วนลดขั้นต่ำ</th>
                <th className="text-systemGrays02LabelSecondary">ประเภท</th>
                <th className="text-systemGrays02LabelSecondary">สถานะ</th>
                <th className="text-systemGrays02LabelSecondary">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingPintoWriterCampaigns && (
                <tr>
                  <td colSpan={7} className="text-center text-colorsBrandWarmBlack02LabelSecondary">
                    กำลังโหลดแคมเปญ...
                  </td>
                </tr>
              )}
              {!isLoadingPintoWriterCampaigns && campaigns.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-colorsBrandWarmBlack02LabelSecondary">
                    ไม่พบแคมเปญ
                  </td>
                </tr>
              )}
              {campaigns.map((campaign) => (
                <tr key={campaign._id}>
                  <td className="w-[320px] min-w-[320px] max-w-[320px]">
                    <div className="relative block w-full" onMouseEnter={() => handlePreviewMouseEnter(campaign._id)} onMouseLeave={handlePreviewMouseLeave}>
                      <Link to={getCampaignTitleLink(campaign._id, campaign.status)} className="block w-full font-semibold underline text-colorsBrandRed01Primary truncate">
                        {campaign.title}
                      </Link>

                      {previewCampaignId === campaign._id && (
                        <div className="absolute left-0 z-30 p-8 mt-2 border rounded-lg shadow-lg top-full bg-componentsBgGrouped02 border-colorsBrandWarmBlack04LabelQuaternary w-[280px]">
                          <div className="mb-6 text-xs font-semibold text-systemGrays02LabelSecondary">ตัวอย่างแบนเนอร์แคมเปญ</div>
                          <img
                            className="w-full h-[120px] rounded-md object-cover"
                            src={campaign.bannerImageUrl || images.pinto.default.bannerCover}
                            alt={`ตัวอย่างแบนเนอร์ ${campaign.title}`}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="text-sm">
                      {formatDate(campaign.registrationStartedAt)} - {formatDate(campaign.registrationEndedAt)}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="text-sm">
                      {formatDate(campaign.startedAt)} - {formatDate(campaign.endedAt)}
                    </div>
                  </td>
                  <td className="text-center">{campaign.minimumDiscountRatio ? `${Math.round(campaign.minimumDiscountRatio * 100)}%` : '-'}</td>
                  <td className="capitalize">{campaign.userType}</td>
                  <td>
                    <span className={CAMPAIGN_STATUS_CLASS_NAME[campaign.status]}>{CAMPAIGN_STATUS[campaign.status]}</span>
                  </td>
                  <td>
                    <div className="flex gap-8">
                      {campaign.status === PintoProductCampaignStatus.Pending && (
                        <>
                          <Link to={`/writerRegisterCampaign/edit?id=${campaign._id}`}>
                            <button className="btn btn-ghost btn-sm" title="แก้ไข">
                              <FiEdit2 />
                            </button>
                          </Link>
                          <button className="btn btn-ghost btn-sm text-colorsBrandRed01Primary" title="ลบ" onClick={() => handleDeleteClick(campaign._id)}>
                            <FiTrash2 />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between mt-12">
            <span className="text-systemGrays02LabelSecondary">แสดงผล 10 รายการต่อหน้า</span>

            {(pintoWriterCampaigns?.totalCount || 0) > 0 && (
              <Pagination currentPage={currentPage} totalCount={pintoWriterCampaigns?.totalCount || 0} pageSize={PAGE_LIMIT} onPageChange={handleChangePage} />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default WriterRegisterCampaignPage;
